import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import EmployerLogo from "../components/EmployerLogo";
import { MapPin, ArrowRight, ArrowUpRight, Star, Briefcase } from "lucide-react";

const SITE_URL = "https://railwaycareers.co.uk";

function formatSalary(v) {
  if (v.salary_min && v.salary_max) return `£${v.salary_min.toLocaleString()}–£${v.salary_max.toLocaleString()}`;
  if (v.salary_min) return `From £${v.salary_min.toLocaleString()}`;
  return "Competitive";
}

const RELATED_LINKS = [
  { to: "/train-driver-jobs", label: "Train driver jobs UK", desc: "Cornerstone guide — salary, training, assessments." },
  { to: "/careers/conductor", label: "Conductor jobs", desc: "£27k → £47k. Common rail-industry entry route." },
  { to: "/careers/engineering", label: "Engineering & Signalling jobs", desc: "£32k → £75k+. Track, S&T, rolling stock." },
  { to: "/depot-finder", label: "UK Vacancy Map", desc: "Filter live jobs by drive time from your postcode." },
];

export default function RegionJobs() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/jobs/region/${slug}`)
      .then((r) => setData(r.data))
      .catch((err) => { if (err.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  const cityName = data?.name;
  const liveCount = data?.live_count ?? 0;

  useSeo({
    title: cityName ? `Rail Jobs in ${cityName} | ${liveCount} Live UK Railway Vacancies` : "Rail jobs in your city · Railway Careers UK",
    description: cityName
      ? `${liveCount} live UK rail vacancies in ${cityName} — train driver, conductor, engineering, signalling and depot roles. Updated daily, free to apply.`
      : "Live UK rail vacancies by city.",
    canonical: `/jobs/${slug}`,
    jsonLd: data && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Live vacancies", item: `${SITE_URL}/vacancies` },
            { "@type": "ListItem", position: 3, name: `Rail jobs in ${cityName}`, item: `${SITE_URL}/jobs/${slug}` },
          ],
        },
        {
          "@type": "ItemList",
          name: `Rail jobs in ${cityName}`,
          numberOfItems: liveCount,
          itemListElement: (data.vacancies || []).slice(0, 25).map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/vacancies/${v.id}`,
            name: v.title,
          })),
        },
      ],
    },
  });

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-sm text-muted-foreground">Loading {slug} jobs…</div>;
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <MapPin className="w-12 h-12 text-muted-foreground mx-auto" />
          <h1 className="font-display font-bold text-ink text-2xl mt-4">City not recognised</h1>
          <p className="text-muted-foreground mt-2 text-sm">We don't currently have a dedicated page for "{slug}". Try the main vacancy search or the Vacancy Map.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"><Link to="/vacancies">Search all vacancies</Link></Button>
            <Button asChild variant="outline"><Link to="/depot-finder">Open Vacancy Map</Link></Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen" data-testid="region-jobs-page">
      {/* Hero */}
      <section className="bg-navy text-bone">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <nav className="text-[11px] text-bone/55 mb-4">
            <Link to="/" className="hover:text-bone">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/vacancies" className="hover:text-bone">Live vacancies</Link>
            <span className="mx-2">/</span>
            <span className="text-bone">{cityName}</span>
          </nav>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber font-semibold">
            <MapPin className="w-3.5 h-3.5" /> {cityName} — UK rail jobs
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight leading-[1.05] mt-3">
            Rail jobs in {cityName}.
          </h1>
          <p className="text-bone/80 mt-5 text-lg leading-relaxed max-w-3xl">
            <strong className="text-bone">{liveCount}</strong> live UK rail vacanc{liveCount === 1 ? "y" : "ies"} in and around {cityName}{liveCount > 0 ? ", from train driver and conductor roles to engineering, signalling and depot positions." : "."} Updated daily.
          </p>
          <p className="text-bone/65 mt-4 text-base leading-relaxed max-w-3xl">{data.lead}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild className="bg-amber text-ink hover:bg-lime-600 hover:text-ink font-semibold" data-testid="region-cta-vacancies">
              <a href="#vacancies">See {liveCount} live role{liveCount === 1 ? "" : "s"} <ArrowRight className="ml-2 w-4 h-4" /></a>
            </Button>
            <Button asChild variant="outline" className="border-amber text-amber hover:bg-amber hover:text-ink">
              <Link to="/depot-finder">Vacancy Map near {cityName}</Link>
            </Button>
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16 space-y-12">
        {/* Vacancies */}
        <section id="vacancies" className="scroll-mt-24">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display font-bold text-ink text-2xl md:text-3xl tracking-tight">
              Live rail vacancies in {cityName}
            </h2>
            <div className="text-xs text-muted-foreground font-mono">{liveCount} live</div>
          </div>

          {data.vacancies.length === 0 ? (
            <div className="mt-5 border border-border rounded-lg p-8 bg-card">
              <Briefcase className="w-8 h-8 text-muted-foreground" />
              <div className="font-display font-bold text-ink text-lg mt-3">No live vacancies in {cityName} right now</div>
              <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
                Rail recruitment moves quickly. Subscribe to the free Monday Briefing and we'll email you the moment a {cityName}-based role goes live.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild className="bg-navy text-bone hover:bg-lime-600 hover:text-ink"><Link to="/">Get the Monday Briefing</Link></Button>
                <Button asChild variant="outline"><Link to="/vacancies">Browse all UK vacancies</Link></Button>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-3" data-testid="region-vacancies">
              {data.vacancies.map((v) => (
                <Link
                  key={v.id}
                  to={`/vacancies/${v.id}`}
                  className="block border border-border rounded-lg p-4 bg-card hover:border-amber transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <EmployerLogo name={v.employer_name} size={36} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-ink line-clamp-1 group-hover:text-amber-700">
                        {v.featured && <Star className="inline w-3.5 h-3.5 fill-amber text-amber mr-1.5 align-middle" />}
                        {v.title}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <span className="font-semibold text-ink/80">{v.employer_name}</span>
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.location}</span>
                        <span className="font-mono">{formatSalary(v)}</span>
                        <span className="capitalize">{(v.employment_type || "full-time").replace("-", " ")}</span>
                      </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-700 shrink-0 mt-1" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Related — internal-link goldmine */}
        <section className="border-t border-border pt-10">
          <h2 className="font-display font-bold text-ink text-2xl tracking-tight">Keep exploring</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {RELATED_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="block border border-border rounded-lg p-4 bg-card hover:border-amber transition-colors group">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-ink group-hover:text-amber-700">{l.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{l.desc}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-700 shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
